/* Bridal Quote Studio™ — public renderer.

  Renders the operator's premium quote template ABOVE the standard quote
  interaction UI on the public client page. We intentionally do NOT rewrite
  the accept/sign/pay flow: that lives in PublicClient.jsx and is reused
  inline. This component is purely presentational.

  Sections rendered in `sections_order` array — each section can be toggled
  off; the "quote" section is a placeholder that displays an intro + an
  anchor scroll target. The actual interactive quote block stays where it
  was inside PublicClient.jsx and is auto-positioned just below this view.
*/
import React from "react";
import { Mail, Phone, Instagram, Globe, MapPin } from "lucide-react";

const FONT_STACKS = {
  serif: '"Cormorant Garamond", "Playfair Display", Georgia, serif',
  sans:  '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
};

/* Static UI strings — the only labels NOT user-typed (everything else is
 * stored as Italian source and translated server-side by GPT in
 * _translate_quote_template). Keys must mirror what's used below. We
 * ship a tight 4-language matrix matching what the rest of the app's
 * PDFs / emails support today (it/en/es/fr). */
const STATIC_LABELS = {
  it: {
    presentation:  "Presentazione",
    gallery:       "Galleria",
    dedicated:     "Proposta dedicata",
    stay_in_touch: "Restiamo in contatto",
  },
  en: {
    presentation:  "About me",
    gallery:       "Gallery",
    dedicated:     "Dedicated proposal",
    stay_in_touch: "Let's stay in touch",
  },
  es: {
    presentation:  "Presentación",
    gallery:       "Galería",
    dedicated:     "Propuesta dedicada",
    stay_in_touch: "Sigamos en contacto",
  },
  fr: {
    presentation:  "Présentation",
    gallery:       "Galerie",
    dedicated:     "Proposition dédiée",
    stay_in_touch: "Restons en contact",
  },
};
const labelOf = (lang, key) => (STATIC_LABELS[lang] || STATIC_LABELS.it)[key] || STATIC_LABELS.it[key];

export default function BridalQuoteStudioView({ tpl, ownerName, language = "it" }) {
  if (!tpl || tpl.enabled === false) return null;

  const lang = (language || "it").toLowerCase();
  const order = (tpl.sections_order || []).filter((k) => (tpl[k] || {}).enabled);
  const theme = tpl.theme || {};

  // Granular color tokens with sensible fallbacks (back-compat with old
  // templates that only had primary_color/accent_color).
  const tokens = {
    primary:        theme.primary_color   || "#0f172a",  // cover bg + contacts bg
    accent:         theme.accent_color    || "#b8895a",  // gold accent (legacy)
    pageBg:         theme.page_bg         || "#ffffff",
    sectionBgAlt:   theme.section_bg_alt  || "#f8f6f2",
    textPrimary:    theme.text_primary    || "#1f1d1a",
    sectionTitle:   theme.section_title   || "#0f172a",
    subtitle:       theme.subtitle_color  || theme.accent_color || "#b8895a",
    divider:        theme.divider_color   || theme.accent_color || "#b8895a",
    buttonBg:       theme.button_bg       || theme.accent_color || "#b8895a",
    buttonText:     theme.button_text     || "#ffffff",
  };
  const font = FONT_STACKS[theme.font_family] || FONT_STACKS.serif;

  return (
    <div
      className="bridal-quote-studio"
      style={{
        // Scoped CSS variables — every nested element reads them with var(--qs-…)
        "--qs-primary":       tokens.primary,
        "--qs-accent":        tokens.accent,
        "--qs-page-bg":       tokens.pageBg,
        "--qs-section-bg":    tokens.sectionBgAlt,
        "--qs-text":          tokens.textPrimary,
        "--qs-section-title": tokens.sectionTitle,
        "--qs-subtitle":      tokens.subtitle,
        "--qs-divider":       tokens.divider,
        "--qs-button-bg":     tokens.buttonBg,
        "--qs-button-text":   tokens.buttonText,
        "--qs-font":          font,
        fontFamily: font,
        background: tokens.pageBg,
        color: tokens.textPrimary,
      }}
      data-testid="public-bqs-root"
    >
      {order.map((key) => {
        if (key === "cover")     return <CoverSection key={key} sec={tpl.cover} ownerName={ownerName} />;
        if (key === "about")     return <AboutSection key={key} sec={tpl.about} lang={lang} />;
        if (key === "journey")   return <JourneySection key={key} sec={tpl.journey} />;
        if (key === "portfolio") return <PortfolioSection key={key} sec={tpl.portfolio} lang={lang} />;
        if (key === "quote")     return <QuoteIntroSection key={key} sec={tpl.quote} lang={lang} />;
        if (key === "contacts")  return <ContactsSection key={key} sec={tpl.contacts} lang={lang} />;
        return null;
      })}
    </div>
  );
}


/* -------- Reusable bits -------- */
function SectionEyebrow({ children }) {
  if (!children) return null;
  return (
    <p
      className="uppercase text-[10px] tracking-[0.4em] mb-3"
      style={{ color: "var(--qs-subtitle)" }}
    >
      — {children} —
    </p>
  );
}

function SectionTitle({ children }) {
  return (
    <h2
      className="leading-tight"
      style={{
        fontSize: "clamp(1.8rem, 4vw, 2.8rem)",
        color: "var(--qs-section-title)",
        fontWeight: 300,
      }}
    >
      {children}
    </h2>
  );
}


/* -------- Cover -------- */
function CoverSection({ sec, ownerName }) {
  const title = (sec.title || ownerName || "").trim();
  const subtitle = (sec.subtitle || "").trim();
  return (
    <section
      className="relative w-full overflow-hidden"
      style={{ background: "var(--qs-primary)", color: "#fff" }}
      data-testid="public-bqs-cover"
    >
      <div className="relative h-[60vh] sm:h-[70vh] min-h-[420px]">
        {sec.image_url && (
          <img
            src={sec.image_url} alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-70"
          />
        )}
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.20) 0%, rgba(0,0,0,0.55) 100%)" }}
        />
        <div className="relative h-full flex flex-col items-center justify-end text-center px-6 pb-14">
          {sec.logo_url && (
            <img
              src={sec.logo_url} alt={`${title} logo`}
              className="h-16 sm:h-20 mb-6 brightness-0 invert"
            />
          )}
          {title && (
            <h1
              className="leading-tight uppercase tracking-[0.18em] font-light"
              style={{ fontSize: "clamp(2.2rem, 6vw, 4.6rem)" }}
            >
              {title}
            </h1>
          )}
          {subtitle && (
            <p
              className="mt-3 text-xs sm:text-sm uppercase tracking-[0.4em] font-light"
              style={{ color: "var(--qs-subtitle)" }}
            >
              {subtitle}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}


/* -------- About -------- */
function AboutSection({ sec, lang }) {
  const heading = (sec.heading || "Chi sono").trim();
  const body = (sec.body || "").trim();
  return (
    <section
      className="py-20 px-6"
      style={{ background: "var(--qs-page-bg)" }}
      data-testid="public-bqs-about"
    >
      <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-5 gap-10 items-start">
        {sec.photo_url && (
          <div className="md:col-span-2">
            <div className="aspect-[3/4] overflow-hidden rounded-sm">
              <img src={sec.photo_url} alt="" className="w-full h-full object-cover" />
            </div>
          </div>
        )}
        <div className={sec.photo_url ? "md:col-span-3" : "md:col-span-5"}>
          <SectionEyebrow>{labelOf(lang, "presentation")}</SectionEyebrow>
          <div className="mb-6">
            <SectionTitle>{heading}</SectionTitle>
          </div>
          {body && (
            <div
              className="text-base leading-relaxed whitespace-pre-wrap"
              style={{ maxWidth: "62ch", color: "var(--qs-text)" }}
            >
              {body}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}


/* -------- Il Percorso Bridal — 4 alternating narrative blocks -------- */
function JourneySection({ sec }) {
  const heading = (sec.heading || "Il Percorso Bridal").trim();
  const eyebrow = (sec.eyebrow || "Il viaggio insieme").trim();
  const blocks = [
    { key: "intro_block",       data: sec.intro_block || {} },
    { key: "trial_block",       data: sec.trial_block || {} },
    { key: "wedding_day_block", data: sec.wedding_day_block || {} },
    { key: "products_block",    data: sec.products_block || {} },
  ].filter((b) => (b.data.enabled !== false) && ((b.data.body || "").trim() || (b.data.heading || "").trim() || b.data.image_url));

  if (blocks.length === 0) return null;

  return (
    <section
      className="py-20 px-6"
      style={{ background: "var(--qs-section-bg)" }}
      data-testid="public-bqs-journey"
    >
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-14">
          <SectionEyebrow>{eyebrow}</SectionEyebrow>
          <SectionTitle>{heading}</SectionTitle>
          <div className="mt-6 flex justify-center">
            <span style={{ width: 48, height: 1, background: "var(--qs-divider)" }} />
          </div>
        </div>

        <div className="space-y-16">
          {blocks.map((b, i) => {
            const reverse = i % 2 === 1;
            const idx = String(i + 1).padStart(2, "0");
            return (
              <article
                key={b.key}
                className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center ${reverse ? "md:[&>*:first-child]:order-2" : ""}`}
                data-testid={`public-bqs-journey-${b.key}`}
              >
                {b.data.image_url ? (
                  <div className="md:col-span-5">
                    <div className="aspect-[4/5] overflow-hidden rounded-sm shadow-[0_8px_30px_rgba(0,0,0,0.08)]">
                      <img src={b.data.image_url} alt={b.data.heading || ""} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                  </div>
                ) : (
                  <div className="md:col-span-5 hidden md:block" />
                )}
                <div className={b.data.image_url ? "md:col-span-7" : "md:col-span-12"}>
                  <p
                    className="font-mono text-[11px] tracking-[0.24em] mb-3"
                    style={{ color: "var(--qs-subtitle)" }}
                  >
                    {idx}
                  </p>
                  {b.data.heading && (
                    <h3
                      className="leading-tight mb-4"
                      style={{
                        fontSize: "clamp(1.4rem, 3vw, 2rem)",
                        color: "var(--qs-section-title)",
                        fontWeight: 300,
                      }}
                    >
                      {b.data.heading}
                    </h3>
                  )}
                  <span
                    className="block mb-5"
                    style={{ width: 28, height: 1, background: "var(--qs-divider)" }}
                  />
                  {b.data.body && (
                    <p
                      className="text-base leading-relaxed whitespace-pre-wrap"
                      style={{ color: "var(--qs-text)", maxWidth: "58ch" }}
                    >
                      {b.data.body}
                    </p>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}


/* -------- Portfolio -------- */
function PortfolioSection({ sec, lang }) {
  const heading = (sec.heading || "Portfolio").trim();
  const images = sec.images || [];
  if (images.length === 0) return null;
  return (
    <section
      className="py-20 px-6"
      style={{ background: "var(--qs-section-bg)" }}
      data-testid="public-bqs-portfolio"
    >
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <SectionEyebrow>{labelOf(lang, "gallery")}</SectionEyebrow>
          <SectionTitle>{heading}</SectionTitle>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {images.map((im, i) => (
            <figure
              key={i}
              className="overflow-hidden bg-white rounded-sm"
              data-testid={`public-bqs-portfolio-img-${i}`}
            >
              <div className="aspect-square">
                <img src={im.url} alt={im.caption || ""} className="w-full h-full object-cover transition-transform duration-700 hover:scale-[1.04]" loading="lazy" />
              </div>
              {im.caption && (
                <figcaption className="px-2 py-1.5 text-[10px] text-center italic" style={{ color: "var(--qs-text)" }}>{im.caption}</figcaption>
              )}
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}


/* -------- Quote intro — leads into the existing interactive block below -------- */
function QuoteIntroSection({ sec, lang }) {
  const heading = (sec.heading || "Preventivo").trim();
  const intro = (sec.intro || "").trim();
  return (
    <section
      className="pt-20 pb-8 px-6"
      style={{ background: "var(--qs-page-bg)" }}
      data-testid="public-bqs-quote-intro"
    >
      <div className="max-w-3xl mx-auto text-center">
        <SectionEyebrow>{labelOf(lang, "dedicated")}</SectionEyebrow>
        <div className="mb-6">
          <SectionTitle>{heading}</SectionTitle>
        </div>
        {intro && (
          <p className="text-base leading-relaxed italic max-w-2xl mx-auto" style={{ color: "var(--qs-text)" }}>
            {intro}
          </p>
        )}
        <div className="mt-8 flex justify-center">
          <span style={{ width: 40, height: 1, background: "var(--qs-divider)" }} />
        </div>
        {/* The actual servizi/prezzi/optional/totale/CTA is rendered by
            PublicClient.jsx immediately below this component — keeping the
            interaction logic single-sourced. */}
      </div>
    </section>
  );
}


/* -------- Contacts -------- */
function ContactsSection({ sec, lang }) {
  const heading = (sec.heading || "Contatti").trim();
  const items = [
    sec.phone     && { icon: Phone,     label: sec.phone,     href: `tel:${sec.phone.replace(/\s/g, "")}` },
    sec.email     && { icon: Mail,      label: sec.email,     href: `mailto:${sec.email}` },
    sec.instagram && { icon: Instagram, label: sec.instagram.replace(/^@/, "@"), href: sec.instagram.startsWith("http") ? sec.instagram : `https://instagram.com/${sec.instagram.replace(/^@/, "")}` },
    sec.website   && { icon: Globe,     label: sec.website,   href: sec.website.startsWith("http") ? sec.website : `https://${sec.website}` },
    sec.address   && { icon: MapPin,    label: sec.address,   href: null },
  ].filter(Boolean);
  if (items.length === 0) return null;

  return (
    <section
      className="py-20 px-6"
      style={{ background: "var(--qs-primary)", color: "#fff" }}
      data-testid="public-bqs-contacts"
    >
      <div className="max-w-3xl mx-auto text-center">
        <p
          className="uppercase text-[10px] tracking-[0.4em] mb-3"
          style={{ color: "var(--qs-subtitle)" }}
        >— {labelOf(lang, "stay_in_touch")} —</p>
        <h2
          className="leading-tight mb-10"
          style={{ fontSize: "clamp(1.8rem, 4vw, 2.8rem)", fontWeight: 300 }}
        >
          {heading}
        </h2>
        <ul className="space-y-4 inline-block text-left">
          {items.map((it, i) => {
            const Icon = it.icon;
            const inner = (
              <span className="flex items-center gap-3 text-base">
                <Icon className="w-4 h-4 shrink-0" strokeWidth={1.5} style={{ color: "var(--qs-subtitle)" }} />
                {it.label}
              </span>
            );
            return (
              <li key={i} data-testid={`public-bqs-contact-${i}`}>
                {it.href
                  ? <a href={it.href} target="_blank" rel="noreferrer" className="hover:opacity-80 transition-opacity">{inner}</a>
                  : inner}
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
